import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import sessionManager from './utils/sessionManager';
import Logo from './components/Logo';
import LoginOverlay from './components/LoginOverlay';
import SiteHeader from './components/SiteHeader';
import API_CONFIG from './config';
// import FastMapComponent from './components/FastMapComponent'; // Temporarily unused
import heroImage from './hero-image.jpg';
import './HomePage.css';
import MapEmbed from './components/MapEmbed';
import PivotTable from './components/PivotTable';
import { filterPivotRows } from './utils/pivotUtils';

// Typing animation component
const TypingWord = ({ text, speed = 50, delay = 200 }) => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentIndex < text.length) {
        setDisplayText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }
    }, speed);

    return () => clearTimeout(timer);
  }, [currentIndex, text, speed]);

  return <span>{displayText}</span>;
};

// Standalone Pivot Table Component
const ListingDetailsPivotTable = ({ 
  pivotData, 
  handleCellClick, 
  postalCodeFilter, 
  selectedCity, 
  selectedArea,
  postalCodeAreas,
  mapLocation 
}) => {
  if (!pivotData || !pivotData.pivot_table) {
    return (
      <div className="pivot-table-container">
        <div className="pivot-summary">
          <div className="summary-header">
            <div className="filter-summary">No data available</div>
          </div>
          <p>Unable to load pivot table data. Please try refreshing the page.</p>
        </div>
      </div>
    );
  }

  const { pivot_table, bedrooms } = pivotData;

  // Convert pivot_table to array for easier processing
  const rowEntries = filterPivotRows(
    Object.entries(pivot_table).map(([key, data]) => ({
      key,
      label: data.label,
      property_type: data.property_type,
      bedrooms: data.bedrooms
    }))
  );

  // Calculate totals
  const calculateTotals = () => {
    const rowTotals = {};
    const bedroomTotals = {};
    let grandTotal = { count: 0, totalRent: 0 };

    // Initialize bedroom totals
    bedrooms.forEach(bedroom => {
      bedroomTotals[bedroom] = { count: 0, totalRent: 0 };
    });

    // Calculate row and bedroom totals
    rowEntries.forEach(row => {
      rowTotals[row.key] = { count: 0, totalRent: 0 };
      
      bedrooms.forEach(bedroom => {
        const cellData = row.bedrooms[bedroom];
        if (cellData && cellData.count > 0) {
          const totalRentForCell = cellData.average_rent * cellData.count;
          
          // Add to row total
          rowTotals[row.key].count += cellData.count;
          rowTotals[row.key].totalRent += totalRentForCell;
          
          // Add to bedroom total
          bedroomTotals[bedroom].count += cellData.count;
          bedroomTotals[bedroom].totalRent += totalRentForCell;
          
          // Add to grand total
          grandTotal.count += cellData.count;
          grandTotal.totalRent += totalRentForCell;
        }
      });
      
      // Calculate average for row
      if (rowTotals[row.key].count > 0) {
        rowTotals[row.key].average = rowTotals[row.key].totalRent / rowTotals[row.key].count;
      }
    });

    // Calculate averages for bedrooms
    bedrooms.forEach(bedroom => {
      if (bedroomTotals[bedroom].count > 0) {
        bedroomTotals[bedroom].average = bedroomTotals[bedroom].totalRent / bedroomTotals[bedroom].count;
      }
    });

    // Calculate grand total average
    if (grandTotal.count > 0) {
      grandTotal.average = grandTotal.totalRent / grandTotal.count;
    }

    return { rowTotals, bedroomTotals, grandTotal };
  };

  const { rowTotals, bedroomTotals, grandTotal } = calculateTotals();

  // Check if any filters are active
  const hasActiveFilters = () => {
    return (postalCodeFilter && postalCodeFilter.trim().length >= 3) || 
           selectedCity || 
           selectedArea;
  };

  // Get active filter summary for display
  const getFilterSummary = () => {
    const filters = [];
    if (postalCodeFilter && postalCodeFilter.trim().length >= 3) {
      // Postal code mode - show just the postal code (areas are displayed separately)
      let postalCodeText = `📮 Postal Code: ${postalCodeFilter.trim().toUpperCase()}`;
      // Remove the areas from the summary text since they're now shown in their own section
      filters.push(postalCodeText);
    } else {
      // City/Area mode
      if (selectedCity) filters.push(`🌍 City: ${selectedCity}`);
      if (selectedArea) filters.push(`📍 Area: ${selectedArea}`);
    }
    return filters.length > 0 ? filters.join(' • ') : '🌍 All Areas';
  };

  return (
    <div className="pivot-table-container">
      <div className="pivot-summary">
        {hasActiveFilters() && (
          <div className="summary-header">
            <div className="filter-summary-with-map">
              <div className="filter-summary-content">
                <div className="filter-summary">{getFilterSummary()}</div>
                {/* Compact mapped areas list without redundant title */}
                {postalCodeAreas.length > 0 && (
                  <div className="mapped-areas-compact">
                    {postalCodeAreas.map((area, index) => (
                      <span key={index} className="area-tag">
                        {area}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {/* Google Maps embed on the right */}
              <MapEmbed location={mapLocation} />
            </div>
          </div>
        )}
        
        <div className="summary-stats">
          <div className="stat-item">
            <span className="stat-label">Total Properties:</span>
            <span className="stat-value">{pivotData.total_listings?.toLocaleString() || grandTotal.count.toLocaleString()}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Property Types:</span>
            <span className="stat-value">{rowEntries.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Bedroom Types:</span>
            <span className="stat-value">{bedrooms.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Average Rent:</span>
            <span className="stat-value">
              ${grandTotal.count > 0 ? Math.round(grandTotal.average).toLocaleString() : '0'}
            </span>
          </div>
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="pivot-table-wrapper">
        <table className="pivot-table">
          <thead>
            <tr>
              <th className="property-type-header">Property Type</th>
              {bedrooms.map(bedroom => (
                <th key={bedroom} className="bedroom-header">
                  {bedroom === '5+' ? '5+ Bedrooms' : `${bedroom} ${bedroom === '1' ? 'Bedroom' : 'Bedrooms'}`}
                </th>
              ))}
              <th className="total-header">Total</th>
            </tr>
          </thead>
          <tbody>
            {rowEntries.map(row => (
              <tr key={row.key} className="data-row">
                <td className="property-type-cell">
                  <div className="property-type-name">{row.label}</div>
                </td>
                {bedrooms.map(bedroom => {
                  const cellData = row.bedrooms[bedroom];
                  const isEmpty = !cellData || cellData.count === 0;
                  
                  return (
                    <td 
                      key={`${row.key}-${bedroom}`} 
                      className={`data-cell ${isEmpty ? 'empty-cell' : 'filled-cell'}`}
                      onClick={() => !isEmpty && handleCellClick(row.property_type, bedroom, cellData)}
                      style={{ cursor: isEmpty ? 'default' : 'pointer' }}
                    >
                      {!isEmpty ? (
                        <div className="cell-content">
                          <div className="avg-rent">${Math.round(cellData.average_rent).toLocaleString()}</div>
                          <div className="count">({cellData.count} listings)</div>
                          <div className="range">${Math.round(cellData.min_rent).toLocaleString()} - ${Math.round(cellData.max_rent).toLocaleString()}</div>
                        </div>
                      ) : (
                        <div className="empty-content">-</div>
                      )}
                    </td>
                  );
                })}
                <td className="total-cell">
                  {rowTotals[row.key]?.count > 0 ? (
                    <div className="cell-content">
                      <div className="avg-rent">${Math.round(rowTotals[row.key].average).toLocaleString()}</div>
                      <div className="count">({rowTotals[row.key].count} total)</div>
                    </div>
                  ) : (
                    <div className="empty-content">-</div>
                  )}
                </td>
              </tr>
            ))}
            
            {/* Totals Row */}
            <tr className="totals-row">
              <td className="property-type-cell">
                <div className="property-type-name">Total</div>
              </td>
              {bedrooms.map(bedroom => {
                const bedroomTotal = bedroomTotals[bedroom];
                return (
                  <td key={`total-${bedroom}`} className="data-cell">
                    {bedroomTotal?.count > 0 ? (
                      <div className="cell-content">
                        <div className="avg-rent">${Math.round(bedroomTotal.average).toLocaleString()}</div>
                        <div className="count">({bedroomTotal.count} total)</div>
                      </div>
                    ) : (
                      <div className="empty-content">-</div>
                    )}
                  </td>
                );
              })}
              <td className="total-cell">
                {grandTotal.count > 0 ? (
                  <div className="cell-content">
                    <div className="avg-rent">${Math.round(grandTotal.average).toLocaleString()}</div>
                    <div className="count">({grandTotal.count} grand total)</div>
                  </div>
                ) : (
                  <div className="empty-content">-</div>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Mobile Cards View */}
      <div className="pivot-cards">
        {rowEntries.map(row => (
          <div key={row.key} className="property-card">
            <div className="property-card-header">
              <h4 className="property-type-title">{row.label}</h4>
              <div className="property-total">
                {rowTotals[row.key]?.count > 0 ? (
                  <span>
                    ${Math.round(rowTotals[row.key].average).toLocaleString()} avg • {rowTotals[row.key].count} total
                  </span>
                ) : (
                  <span>No listings</span>
                )}
              </div>
            </div>
            
            <div className="bedroom-options">
              {bedrooms.map(bedroom => {
                const cellData = row.bedrooms[bedroom];
                const isEmpty = !cellData || cellData.count === 0;
                
                return (
                  <div 
                    key={`${row.key}-${bedroom}`} 
                    className={`bedroom-option ${isEmpty ? 'empty' : 'filled'}`}
                    onClick={() => !isEmpty && handleCellClick(row.property_type, bedroom, cellData)}
                  >
                    <div className="bedroom-label">
                      {bedroom === '5+' ? '5+ Bedrooms' : `${bedroom} ${bedroom === '1' ? 'Bedroom' : 'Bedrooms'}`}
                    </div>
                    {!isEmpty ? (
                      <>
                        <div className="avg-rent">${Math.round(cellData.average_rent).toLocaleString()}</div>
                        <div className="count">({cellData.count} listings)</div>
                        <div className="range">${Math.round(cellData.min_rent).toLocaleString()} - ${Math.round(cellData.max_rent).toLocaleString()}</div>
                      </>
                    ) : (
                      <div className="empty-content">No listings</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Mobile Totals Card */}
        <div className="property-card">
          <div className="property-card-header">
            <h4 className="property-type-title">📊 Total Summary</h4>
            <div className="property-total">
              ${grandTotal.count > 0 ? Math.round(grandTotal.average).toLocaleString() : '0'} avg • {grandTotal.count} total
            </div>
          </div>
          
          <div className="bedroom-options">
            {bedrooms.map(bedroom => (
              <div key={bedroom} className="bedroom-option empty">
                <div className="bedroom-title">
                  {bedroom === '5+' ? '5+ Bedrooms' : `${bedroom} ${bedroom === '1' ? 'Bedroom' : 'Bedrooms'}`}
                </div>
                {bedroomTotals[bedroom]?.count > 0 ? (
                  <div className="bedroom-content">
                    <div className="avg-rent">${Math.round(bedroomTotals[bedroom].average).toLocaleString()}</div>
                    <div className="count">({bedroomTotals[bedroom].count} total)</div>
                  </div>
                ) : (
                  <div className="empty-bedroom">No data</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const HomePage = () => {
  const navigate = useNavigate();

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Modal state
  const [showLogin, setShowLogin] = useState(false);
  const [loginMode, setLoginMode] = useState('signin'); // 'signin' or 'signup'

  // Database analysis state
  const [pivotData, setPivotData] = useState(null);
  const [pivotLoading, setPivotLoading] = useState(false);
  const [pivotError, setPivotError] = useState(null);
  
  // Map overlay state (same as MapPage)
  const [selectedNeighborhood, setSelectedNeighborhood] = useState(null);
  
  // Cell clicks now redirect to chat page instead of showing listings below
  
  // Filtering state
  const [postalCodeFilter, setPostalCodeFilter] = useState('');
  const [postalCodeInput, setPostalCodeInput] = useState(''); // For display in input field
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  const [selectedDateFilter, setSelectedDateFilter] = useState('all'); // New state for date filter
  const [availableCities, setAvailableCities] = useState([]);
  const [availableAreas, setAvailableAreas] = useState([]);
  const [postalCodeAreas, setPostalCodeAreas] = useState([]);
  
  // Google Maps state for homepage map
  const googleMapContainer = useRef(null);
  const googleMapRef = useRef(null);
  const googleMarkersRef = useRef([]);
  const searchBoxRef = useRef(null);
  const searchInputRef = useRef(null);
  const [googleApiKey, setGoogleApiKey] = useState('');
  const USE_GOOGLE_BASEMAP = !!googleApiKey;
  
  // Fetch Google Maps API Key the same way as MapPage
  useEffect(() => {
    const fetchApiKey = async () => {
      try {
        const resp = await fetch(`${API_CONFIG.BASE_URL}/google-api-key`);
        if (resp.ok) {
          const data = await resp.json();
          if (data.success && data.apiKey) {
            setGoogleApiKey(data.apiKey);
            return;
          }
        }
      } catch (e) {
        // ignore and try fallback
      }

      // Fallback: extract key from embed proxy
      try {
        const embedResp = await fetch(`${API_CONFIG.BASE_URL}/google-maps-embed?location=Toronto`);
        if (embedResp.ok) {
          const html = await embedResp.text();
          const m = html.match(/key=([^&"']+)/);
          if (m && m[1]) {
            setGoogleApiKey(m[1]);
          }
        }
      } catch (e) {
        // leave key empty; map will not be shown
      }
    };
    fetchApiKey();
  }, []);

  // Check authentication status on component mount
  useEffect(() => {
    const checkAuth = () => {
      const authenticated = sessionManager.isAuthenticated();
      setIsAuthenticated(authenticated);
    };
    
    checkAuth();
    
    // Listen for session changes (e.g., from other tabs)
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  // Fetch pivot data by neighborhood IDs (used for Google markers)
  const fetchPivotByNeighborhoodIds = useCallback(async (neighborhoodIds, cityName) => {
    try {
      const ids = Array.isArray(neighborhoodIds) ? neighborhoodIds : [neighborhoodIds];
      const params = new URLSearchParams();
      if (ids.length > 0) params.append('neighborhood_id', ids.join(','));
      if (cityName) params.append('city', cityName);
      if (selectedDateFilter !== 'all') params.append('date_filter', selectedDateFilter);
      const url = `https://nk2lgsxkwf.execute-api.us-east-1.amazonaws.com/prod/listing-details-pivot?${params.toString()}`;
      const resp = await fetch(url);
      const data = await resp.json();
      if (resp.ok && data.success) return data.data;
    } catch (e) {
      console.error('Error fetching pivot by neighborhood IDs:', e);
    }
    return null;
  }, [selectedDateFilter]);

  // Function to fetch listing details pivot data
  const fetchListingDetailsPivot = useCallback(async (filters = {}) => {
    setPivotLoading(true);
    setPivotError(null);
    
    try {
      // Build URL with active filters
      const searchParams = new URLSearchParams();
      
      // Location filters - postal code takes precedence over city/area
      if (filters.postal_code && filters.postal_code.trim().length >= 3) {
        searchParams.append('area_postal_code', filters.postal_code.trim().toUpperCase());
      } else {
        // Use city + area filters (only when no postal code)
        if (filters.city) {
          searchParams.append('city', filters.city);
        }
        
        if (filters.area) {
          searchParams.append('neighborhood', filters.area);
        }
      }
      
      // Date filter
      if (filters.date_filter && filters.date_filter !== 'all') {
        searchParams.append('days_filter', filters.date_filter);
      }
      
      const url = `https://nk2lgsxkwf.execute-api.us-east-1.amazonaws.com/prod/listing-details-pivot?${searchParams}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setPivotData(data.data);
        
        // Update filter options from API response
        if (data.data && data.data.filter_options) {
          setAvailableCities(data.data.filter_options.cities || []);
          setAvailableAreas(data.data.filter_options.neighborhoods || []);
        }
        
      } else {
        setPivotError(data.message || 'Failed to fetch pivot data');
      }
    } catch (error) {
      console.error('Error fetching pivot data:', error);
      setPivotError('Network error. Please check your connection and try again.');
    } finally {
      setPivotLoading(false);
    }
  }, []);

  const handleShowLoginModal = (mode) => {
    setLoginMode(mode);
    setShowLogin(true);
  };

  const handleCloseLoginModal = () => {
    setShowLogin(false);
    // Re-check authentication status after modal closes
    const authenticated = sessionManager.isAuthenticated();
    setIsAuthenticated(authenticated);
  };

  // Handle cell click - redirect to chat page
  const handleCellClick = async (propertyType, bedroomCount, cellData) => {
    // Build search parameters for chat page
    const searchParams = new URLSearchParams({
      property_type: propertyType,
      bedrooms: bedroomCount,
      count: cellData.count || '0',
      avg_rent: cellData.average_rent || '0',
      min_rent: cellData.minRent || '0',
      max_rent: cellData.maxRent || '0',
      user_role: 'tenant', // Default to tenant for now
      sources: 'realtor,facebook,kijiji'
    });

    // Add specific location parameters for an accurate backend search
    if (postalCodeFilter && postalCodeFilter.trim().length >= 3) {
      searchParams.append('postal_code', postalCodeFilter.trim().toUpperCase());
      searchParams.append('location_display', postalCodeFilter.trim().toUpperCase());
    } else {
      if (selectedCity) {
        searchParams.append('city', selectedCity);
      }
      if (selectedArea) {
        searchParams.append('area', selectedArea);
      }
      // Set a display name for the greeting message
      searchParams.append('location_display', 
        selectedCity && selectedArea ? `${selectedArea}, ${selectedCity}` :
        selectedCity ? selectedCity : 'Greater Toronto Area'
      );
    }
    
    // Add date filter if not "all"
    if (selectedDateFilter && selectedDateFilter !== 'all') {
      searchParams.append('days_filter', selectedDateFilter);
    }
    
    // Check for authentication before navigating
    if (!isAuthenticated) {
      // For simplicity in this flow, we'll just open the modal.
      // A more advanced implementation would use a return URL after successful login.
      handleShowLoginModal('signup');
    } else {
      // Navigate to chat page with search parameters
      navigate(`/chat?${searchParams.toString()}`);
    }
  };

  // Handle postal code input and auto-populate city/area
  const handlePostalCodeChange = async (e) => {
    const postalCode = e.target.value.trim().toUpperCase();
    setPostalCodeInput(postalCode); // Always update the input display
    
    // Only lookup and filter if we have at least 3 characters (area code) or field is cleared
    if (postalCode.length >= 3) {
      // Update filter state only when we have a valid postal code
      setPostalCodeFilter(postalCode);
      
      try {
        const response = await fetch(`https://nk2lgsxkwf.execute-api.us-east-1.amazonaws.com/prod/postal-code-lookup?postal_code=${encodeURIComponent(postalCode)}`);
        
        if (response.ok) {
          const data = await response.json();
          console.log('📍 Postal code lookup response:', data);
          
          if (data.success && data.data) {
            // Extract neighborhood names from the areas array
            const areas = Array.isArray(data.data.areas) 
              ? data.data.areas.map(area => area.neighborhood || area.name || area)
              : [];
            
            console.log('📍 Mapped areas for', postalCode, ':', areas);
            setPostalCodeAreas(areas);
            
            // Auto-populate city if not already set
            if (!selectedCity && Array.isArray(data.data.cities) && data.data.cities.length > 0) {
              setSelectedCity(data.data.cities[0]);
            }
            
            // Auto-populate area if not already set and areas available
            if (!selectedArea && areas.length > 0) {
              setSelectedArea(areas[0]);
            }
            
          } else {
            console.log('📍 No data returned for postal code:', postalCode);
            setPostalCodeAreas([]);
          }
        } else {
          console.log('📍 Postal code lookup failed with status:', response.status);
          setPostalCodeAreas([]);
        }
      } catch (error) {
        console.error('Error looking up postal code:', error);
        setPostalCodeAreas([]);
      }
      
    } else if (postalCode.length === 0) {
      // Clear everything when field is empty
      setPostalCodeFilter('');
      setPostalCodeAreas([]);
      setSelectedCity('');
      setSelectedArea('');
    } else {
      // Less than 3 characters - just clear areas but don't trigger filtering
      setPostalCodeAreas([]);
    }
  };

  // Handle city change
  const handleCityChange = async (event) => {
    const city = event.target.value;
    setSelectedCity(city);
    setSelectedArea(''); // Reset area when city changes
    
    if (city) {
      try {
        const response = await fetch(`https://nk2lgsxkwf.execute-api.us-east-1.amazonaws.com/prod/city-areas?city=${encodeURIComponent(city)}`);
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setAvailableAreas(data.data.areas || []);
          }
        }
      } catch (error) {
        console.error('Error fetching areas for city:', error);
      }
    } else {
      setAvailableAreas([]);
    }
    
  };

  // Handle area change
  const handleAreaChange = async (event) => {
    const area = event.target.value;
    setSelectedArea(area);
    
  };

  // Handle date filter change
  const handleDateFilterChange = (event) => {
    const dateFilter = event.target.value;
    setSelectedDateFilter(dateFilter);
  };

  // Clear all filters
  const clearAllFilters = async () => {
    setPostalCodeFilter('');
    setPostalCodeInput('');
    setSelectedCity('');
    setSelectedArea('');
    setSelectedDateFilter('all');
    setPostalCodeAreas([]);
    
  };

  // Effect for initial data load
  useEffect(() => {
    fetchListingDetailsPivot({});
  }, [fetchListingDetailsPivot]);

  // Ref to track initial mount and prevent double-fetching
  const isInitialMount = useRef(true);

  // Effect to fetch pivot data when filters change
  useEffect(() => {
    // Skip the initial mount because the initial load is handled by another effect
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // Debounce the fetch call to avoid rapid firing while user is typing/selecting
    const handler = setTimeout(() => {
      const filters = {};
      if (postalCodeFilter && postalCodeFilter.trim().length >= 3) {
        filters.postal_code = postalCodeFilter;
      } else {
        if (selectedCity) filters.city = selectedCity;
        if (selectedArea) filters.area = selectedArea;
      }
      if (selectedDateFilter) filters.date_filter = selectedDateFilter;
      fetchListingDetailsPivot(filters);
    }, 300); // 300ms debounce

    // Cleanup function to clear the timeout if filters change again quickly
    return () => {
      clearTimeout(handler);
    };
  }, [postalCodeFilter, selectedCity, selectedArea, selectedDateFilter, fetchListingDetailsPivot]);

  // Memoize map location to prevent re-renders
  const mapLocation = useMemo(() => {
    if (postalCodeFilter && postalCodeFilter.length >= 3) return `${postalCodeFilter}, Toronto, ON, Canada`;
    if (selectedCity && selectedArea) return `${selectedArea}, ${selectedCity}, ON, Canada`;
    if (selectedCity) return `${selectedCity}, ON, Canada`;
    return null;
  }, [postalCodeFilter, selectedCity, selectedArea]);

  // Handle overlay cell click (same as MapPage)
  const handleOverlayCellClick = useCallback((propertyType, bedroomCount, bedroomData) => {
    setSelectedNeighborhood(null);
    
    const searchParams = new URLSearchParams({
      property_type: propertyType,
      bedrooms: bedroomCount,
      count: bedroomData.count || '0',
      avg_rent: Math.round(bedroomData.average_rent) || '0',
      min_rent: Math.round(bedroomData.min_rent) || '0',
      max_rent: Math.round(bedroomData.max_rent) || '0',
      user_role: 'tenant',
      sources: 'realtor,facebook,kijiji'
    });

    const neighborhood = selectedNeighborhood?.appliedFilters?.neighborhood;
    const city = selectedNeighborhood?.selectedCity || 'Toronto';
    if (neighborhood) {
      searchParams.append('city', city);
      searchParams.append('area', neighborhood);
      searchParams.append('location_display', `${neighborhood}, ${city}`);
    }

    window.location.href = `/chat?${searchParams.toString()}`;
  }, [selectedNeighborhood]);

  // Google Maps initialization effect (copied from MapPage.js)
  useEffect(() => {
    if (!googleMapContainer.current) return;

    if (USE_GOOGLE_BASEMAP) {
      // Load Google Maps JS API and show a simple basemap for visual comparison
      const loadGoogle = () => {
        if (window.google && window.google.maps) return Promise.resolve();
        if (window.__gmapsLoadingPromise) return window.__gmapsLoadingPromise;
        window.__gmapsLoadingPromise = new Promise((resolve, reject) => {
          let script = document.getElementById('google-maps-script');
          if (!script) {
            script = document.createElement('script');
            script.id = 'google-maps-script';
            script.src = `https://maps.googleapis.com/maps/api/js?key=${googleApiKey}&libraries=places`;
            script.async = true;
            script.defer = true;
            script.setAttribute('loading', 'async');
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          } else {
            script.addEventListener('load', resolve, { once: true });
            script.addEventListener('error', reject, { once: true });
          }
        });
        return window.__gmapsLoadingPromise;
      };

      loadGoogle().then(() => {
        googleMapRef.current = new window.google.maps.Map(googleMapContainer.current, {
          center: { lat: 43.6532, lng: -79.3832 },
          zoom: 11,
          mapTypeId: 'roadmap',
          disableDefaultUI: false,
          fullscreenControl: false,
          gestureHandling: 'cooperative',
        });
        console.log('Google Maps basemap initialized for homepage');

        // Add search box functionality
        const createSearchBox = () => {
          // Create search input element
          const input = document.createElement('input');
          input.type = 'text';
          input.placeholder = 'Search for address, landmark, or postal code...';
          input.style.cssText = `
            width: 300px;
            height: 40px;
            margin: 10px;
            padding: 0 12px;
            border: 1px solid #ccc;
            border-radius: 4px;
            font-size: 14px;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            background: white;
            outline: none;
          `;
          searchInputRef.current = input;

          // Create search box
          searchBoxRef.current = new window.google.maps.places.SearchBox(input);
          
          // Add search box to map
          googleMapRef.current.controls[window.google.maps.ControlPosition.TOP_LEFT].push(input);

          // Listen for places changed
          searchBoxRef.current.addListener('places_changed', () => {
            const places = searchBoxRef.current.getPlaces();
            if (places.length === 0) return;

            const place = places[0];
            if (!place.geometry || !place.geometry.location) {
              console.warn('Place has no geometry');
              return;
            }

            // Center map on the selected place
            googleMapRef.current.setCenter(place.geometry.location);
            
            // Adjust zoom based on place type
            if (place.geometry.viewport) {
              googleMapRef.current.fitBounds(place.geometry.viewport);
            } else {
              // Default zoom for specific locations
              googleMapRef.current.setZoom(15);
            }

            console.log(`Map recentered to: ${place.formatted_address || place.name}`);
          });

          // Bias search results to current map bounds
          googleMapRef.current.addListener('bounds_changed', () => {
            if (searchBoxRef.current && googleMapRef.current) {
              searchBoxRef.current.setBounds(googleMapRef.current.getBounds());
            }
          });
        };

        const clearGoogleMarkers = () => {
          if (!googleMarkersRef.current) return;
          googleMarkersRef.current.forEach(m => m.setMap(null));
          googleMarkersRef.current = [];
        };

        const buildCircleSymbol = (count, zoom) => {
          const base = 12; // px radius baseline
          const scale = Math.max(10, Math.min(28, base + (zoom - 10) * 2));
          return {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale,
            fillColor: '#f7931e',
            fillOpacity: 0.95,
            strokeColor: '#ffffff',
            strokeWeight: 2
          };
        };

        let debounceTimer;
        const fetchAndRenderMarkers = async () => {
          try {
            const mapObj = googleMapRef.current;
            if (!mapObj) return;
            const zoom = mapObj.getZoom();
            const bounds = mapObj.getBounds();
            if (!bounds) return;
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            const params = new URLSearchParams({
              zoom: String(zoom),
              south: String(sw.lat()),
              north: String(ne.lat()),
              west: String(sw.lng()),
              east: String(ne.lng())
            });
            const url = `${API_CONFIG.BASE_URL}/map-markers?${params.toString()}`;
            const resp = await fetch(url);
            const data = await resp.json();
            if (!resp.ok || !data.success) return;
            clearGoogleMarkers();
            const info = new window.google.maps.InfoWindow();
            const markers = Array.isArray(data.data) ? data.data : (data.data && data.data.markers ? data.data.markers : []);
            markers.forEach(marker => {
              const position = { lat: Number(marker.latitude), lng: Number(marker.longitude) };
              const count = Number(
                marker.quantity ?? marker.total_listings ?? marker.count ?? 0
              );
              const m = new window.google.maps.Marker({
                position,
                map: mapObj,
                icon: buildCircleSymbol(count, zoom),
                label: {
                  text: String(count),
                  color: '#ffffff',
                  fontWeight: '700'
                },
                title: marker.marker_name
              });
              m.addListener('click', async () => {
                // Parse neighborhood IDs if present
                let ids = [];
                try {
                  if (Array.isArray(marker.neighborhood_ids)) ids = marker.neighborhood_ids;
                  else if (typeof marker.neighborhood_ids === 'string' && marker.neighborhood_ids.trim().length > 0) ids = JSON.parse(marker.neighborhood_ids);
                } catch (_) {}

                // Fallback to showing info window if no IDs
                if (!ids || ids.length === 0) {
                  info.setContent(`<div style="font-weight:600;margin-bottom:4px;max-width:240px">${marker.marker_name}</div><div>${count} listings</div>`);
                  info.open({ anchor: m, map: mapObj, shouldFocus: false });
                  return;
                }

                const pivot = await fetchPivotByNeighborhoodIds(ids, marker.city_name || 'Toronto');
                if (pivot) {
                  setSelectedNeighborhood({
                    showPivotTable: true,
                    pivotData: pivot,
                    selectedNeighborhood: ids.length > 1 ? `Neighborhood IDs: ${ids.join(', ')}` : marker.marker_name,
                    selectedCity: marker.city_name || 'Toronto',
                    clusterNeighborhoods: ids,
                    appliedFilters: {
                      date_filter: selectedDateFilter,
                      neighborhood: marker.marker_name
                    }
                  });
                } else {
                  info.setContent(`<div style="font-weight:600;margin-bottom:4px;max-width:240px">${marker.marker_name}</div><div>${count} listings</div>`);
                  info.open({ anchor: m, map: mapObj, shouldFocus: false });
                }
              });
              googleMarkersRef.current.push(m);
            });
          } catch (e) {
            console.warn('Failed to render Google markers', e);
          }
        };

        const debouncedUpdate = () => {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(fetchAndRenderMarkers, 300);
        };

        // Initial and on interactions
        window.google.maps.event.addListenerOnce(googleMapRef.current, 'idle', fetchAndRenderMarkers);
        googleMapRef.current.addListener('idle', debouncedUpdate);
        
        // Initialize search box
        createSearchBox();
      }).catch((e) => {
        console.error('Failed to load Google Maps API', e);
      });

      return () => {
        // No explicit cleanup needed for Google Maps object
        googleMapRef.current = null;
        searchBoxRef.current = null;
        searchInputRef.current = null;
      };
    }
  }, [USE_GOOGLE_BASEMAP, googleApiKey, fetchPivotByNeighborhoodIds, selectedDateFilter]);

  return (
    <div className="homepage">
      {showLogin && <LoginOverlay initialMode={loginMode} onClose={handleCloseLoginModal} />}
      
      {/* Header */}
      <SiteHeader />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="img-container">
          <img src={heroImage} alt="Toronto skyline" className="hero-img" />
        </div>
        <div className="hero-container">
          <h1 className="hero-title">
            Find Your Perfect Rental with AI
          </h1>
          
          <p className="hero-subtitle">
            Flexible, AI-Powered Rentals Across Canada with Intelligent Matching and 24/7 Support
          </p>

          <button className="hero-chat-btn" onClick={() => navigate('/chat')}>
            <div className="typing-container">
              <div className="logo-icon">
                <Logo size={28} showText={false} variant="light" />
              </div>
              <span className="typing-text">
                Start chatting with the Rental Help AI <TypingWord text="Now" speed={50} delay={200} />
              </span>
            </div>
          </button>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <div className="container">
          <div 
            className="map-container" 
            style={{ position: 'relative', height: '80vh' }}
          >
            <div 
              ref={googleMapContainer} 
              style={{ 
                width: '100%', 
                height: '100%',
                border: '1px solid #ddd',
                borderRadius: '8px'
              }} 
            />
            {selectedNeighborhood && selectedNeighborhood.showPivotTable && (
              <div className="neighborhood-overlay" onClick={() => setSelectedNeighborhood(null)}>
                <div className="neighborhood-overlay-content pivot-table-overlay" onClick={(e) => e.stopPropagation()}>
                  <button className="overlay-close" onClick={() => setSelectedNeighborhood(null)}>×</button>
                  
                  <div style={{textAlign: 'center', marginBottom: '20px'}}>
                    <h2 style={{fontSize: '24px', fontWeight: '600', color: '#333', margin: '0', lineHeight: '1.2'}}>
                      {selectedNeighborhood.appliedFilters?.neighborhood || selectedNeighborhood.selectedNeighborhood}
                    </h2>
                  </div>
                  
                  <PivotTable 
                    pivotData={selectedNeighborhood.pivotData}
                    selectedCity={selectedNeighborhood.selectedCity || 'Toronto'}
                    selectedArea={selectedNeighborhood.appliedFilters?.neighborhood || ''}
                    onCellClick={handleOverlayCellClick}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Rental Listings Database Analysis Section */}
      <section className="database-analysis-section">
        <div className="container">
          <div className="listing-details-section">
            <h3>Rental Listings Statistics in Ontario</h3>
            <p>Interactive data visualization showing rental listings from our database by city and number of bedrooms.</p>
            
            {/* Enhanced Filtering Controls */}
            <div className="enhanced-filters compact">
              <div className="filters-header">
                <h4>🔍 Filter Listings</h4>
                <button 
                  onClick={async () => {
                    await clearAllFilters();
                  }} 
                  disabled={pivotLoading}
                  className="clear-filters-icon"
                  title="Clear All Filters"
                >
                  ✕
                </button>
              </div>
              
              {/* Compact Filter Row */}
              <div className="compact-filters-row">
                {/* Postal Code Filter */}
                <div className="filter-group compact">
                  <label>🏢 Postal Code:</label>
                  <input
                    type="text"
                    value={postalCodeInput}
                    onChange={handlePostalCodeChange}
                    placeholder="M5V, M2N, L3R"
                    maxLength="3"
                    disabled={pivotLoading}
                    style={{textTransform: 'uppercase'}}
                  />
                </div>

                {/* City Filter */}
                <div className="filter-group compact">
                  <label>🌍 City:</label>
                  <select 
                    value={selectedCity}
                    onChange={handleCityChange}
                    disabled={pivotLoading || (postalCodeFilter && postalCodeFilter.trim().length >= 3)}
                  >
                    <option value="">All Cities</option>
                    {availableCities.map(city => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Area Filter */}
                <div className="filter-group compact">
                  <label>📍 Area:</label>
                  <select 
                    value={selectedArea}
                    onChange={handleAreaChange}
                    disabled={pivotLoading || !selectedCity || (postalCodeFilter && postalCodeFilter.trim().length >= 3)}
                  >
                    <option value="">All Areas</option>
                    {availableAreas.map(area => (
                      <option key={area} value={area}>
                        {area}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date Filter */}
                <div className="filter-group compact">
                  <label>📅 Listed:</label>
                  <select 
                    value={selectedDateFilter}
                    onChange={handleDateFilterChange}
                    disabled={pivotLoading}
                  >
                    <option value="all">All</option>
                    <option value="1">Last 1 day</option>
                    <option value="3">Last 3 days</option>
                    <option value="7">Last 7 days</option>
                    <option value="30">Last 30 days</option>
                    <option value="90">Last 90 days</option>
                  </select>
                </div>
              </div>

              {/* Compact Status Messages */}
              {(postalCodeFilter && postalCodeFilter.trim().length >= 3) && (
                <div className="filter-status">
                  <span className="status-icon">📮</span>
                  <span className="status-text">
                    Filtering by postal code: {postalCodeFilter.trim().toUpperCase()}
                    {postalCodeAreas.length > 0 && (
                      <span className="areas-info"> (Areas: {postalCodeAreas.join(', ')})</span>
                    )}
                  </span>
                </div>
              )}

              {selectedCity && !postalCodeFilter && (
                <div className="filter-status">
                  <span className="status-icon">🌍</span>
                  <span className="status-text">
                    Filtering by city: {selectedCity}
                    {selectedArea && <span className="area-info"> - {selectedArea}</span>}
                  </span>
                </div>
              )}
            </div>

            {/* Loading State */}
            {pivotLoading && (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading rental statistics...</p>
              </div>
            )}

            {/* Error State */}
            {pivotError && (
              <div className="error-state">
                <div className="error-icon">⚠️</div>
                <p className="error-message">{pivotError}</p>
                <button 
                  onClick={async () => {
                    await fetchListingDetailsPivot({
                      property_type: '',
                      bedrooms: '',
                      city: selectedCity,
                      area: selectedArea,
                      postal_code: postalCodeFilter
                    });
                  }}
                  className="retry-button"
                >
                  Retry
                </button>
              </div>
            )}

            {/* Pivot Table Display */}
            {!pivotLoading && !pivotError && (
              <ListingDetailsPivotTable 
                pivotData={pivotData}
                handleCellClick={handleCellClick}
                postalCodeFilter={postalCodeFilter}
                selectedCity={selectedCity}
                selectedArea={selectedArea}
                postalCodeAreas={postalCodeAreas}
                mapLocation={mapLocation}
              />
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="homepage-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <Logo size="medium" showText={true} variant="dark" />
              <p>© 2025 RentalHelp.AI. All rights reserved.</p>
            </div>
            
            <div className="footer-links">
              <div className="footer-column">
                <h4>Product</h4>
                <ul>
                  <li><a href="#features">Features</a></li>
                  <li><a href="#pricing">Pricing</a></li>
                  <li><a href="#how-it-works">How it Works</a></li>
                </ul>
              </div>
              
              <div className="footer-column">
                <h4>Support</h4>
                <ul>
                  <li><a href="#help">Help Center</a></li>
                  <li><a href="#contact">Contact</a></li>
                  <li><a href="#faq">FAQ</a></li>
                </ul>
              </div>
              
              <div className="footer-column">
                <h4>Legal</h4>
                <ul>
                  <li><a href="#terms">Terms</a></li>
                  <li><a href="#privacy">Privacy</a></li>
                  <li><a href="#cookies">Cookies</a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage; 
